import { format } from 'date-fns';
import { enUS, he } from 'date-fns/locale';

const locales = {
  en: enUS,
  he: he,
};

/**
 * Format a date using the appropriate locale
 * @param date - The date to format
 * @param formatStr - The format string (e.g., 'MMM d, yyyy')
 * @param locale - The locale to use ('en' or 'he')
 * @returns Formatted date string
 */
export const formatDate = (
  date: Date | string,
  formatStr: string,
  locale: 'en' | 'he' = 'en'
): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return format(dateObj, formatStr, { locale: locales[locale] });
};

/**
 * Format a date with default format for the application
 * @param date - The date to format
 * @param locale - The locale to use ('en' or 'he')
 * @returns Formatted date string (e.g., 'Jan 1, 2024' or 'ינו׳ 1, 2024')
 */
export const formatDefaultDate = (
  date: Date | string,
  locale: 'en' | 'he' = 'en'
): string => {
  return formatDate(date, 'MMM d, yyyy', locale);
};

const YMD_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse a date-only string (YYYY-MM-DD) as a local-time Date. Returns null if
 * the value is missing, malformed, or invalid. Use this instead of `new Date(value)`
 * for date-only values — that path treats the string as UTC midnight and shows
 * the wrong day in negative-offset locales.
 */
export const parseLocalYmd = (value: string | undefined): Date | null => {
  if (!value) return null;
  const match = YMD_PATTERN.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Format a Date as a YYYY-MM-DD string using local-time components. Pair with
 * `parseLocalYmd` to round-trip a date-only value without timezone drift.
 */
export const formatLocalYmd = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
